import dayjs from 'dayjs';
import { tx } from '../../i18n/translate.js';

export interface SyncJob {
  id: string;
  triggerType: string;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  summary: unknown;
  errorMessage: string | null;
  triggeredBy: { name: string | null; email: string | null } | null;
}

export interface SyncDiff {
  id: string;
  entityType: string;
  changeType: string;
  displayName: string;
  secondaryText: string | null;
  beforePayload: unknown;
  afterPayload: unknown;
}

const USER_FIELDS = ['fullName', 'email', 'domainName', 'businessUnitNames', 'status', 'syncState'];
const DEPARTMENT_FIELDS = ['name', 'parentName', 'status', 'sortOrder'];

export function formatTime(value: string | null | undefined) {
  return value
    ? dayjs(value).format('YYYY-MM-DD HH:mm')
    : tx('legacy:pages.admin.butreepage.notScheduled');
}

export function formatDuration(value: number | null | undefined) {
  if (!value) return '--';
  if (value < 60_000) return `${Math.round(value / 1000)}s`;
  return `${Math.floor(value / 60_000)}m ${Math.round((value % 60_000) / 1000)}s`;
}

export function statusLabel(status: string) {
  if (status === 'success') return tx('legacy:pages.admin.butreepage.syncStatusSuccess');
  if (status === 'failed') return tx('legacy:pages.admin.butreepage.syncStatusFailed');
  return tx('legacy:pages.admin.butreepage.syncStatusRunning');
}

export function statusColor(status: string) {
  if (status === 'success') return 'success';
  if (status === 'failed') return 'error';
  return 'processing';
}

export function entityLabel(value: string) {
  return value === 'department'
    ? tx('legacy:pages.admin.butreepage.entityDepartment')
    : tx('legacy:pages.admin.butreepage.entityUser');
}

export function changeLabel(value: string) {
  const map: Record<string, string> = {
    created: tx('legacy:pages.admin.butreepage.changeCreated'),
    updated: tx('legacy:pages.admin.butreepage.changeUpdated'),
    disabled: tx('legacy:pages.admin.butreepage.changeDisabled'),
    restored: tx('legacy:pages.admin.butreepage.changeRestored'),
  };
  return map[value] ?? value;
}

export function changeColor(value: string) {
  if (value === 'created' || value === 'restored') return 'green';
  if (value === 'disabled') return 'red';
  return 'blue';
}

export function summaryLine(job: SyncJob) {
  if (job.status === 'failed') {
    return job.errorMessage ?? tx('legacy:pages.admin.butreepage.syncStatusFailed');
  }
  const summary = summaryRecord(job);
  const total = asNumber(summary?.total);
  if (!total) return tx('legacy:pages.admin.butreepage.noActualChanges');
  return tx('legacy:pages.admin.butreepage.changeSummaryLine', {
    total,
    users: summaryCount(job, 'users'),
    departments: summaryCount(job, 'departments'),
  });
}

export function fieldRows(diff: SyncDiff) {
  const before = asRecord(diff.beforePayload);
  const after = asRecord(diff.afterPayload);
  const fields = diff.entityType === 'department' ? DEPARTMENT_FIELDS : USER_FIELDS;
  const rows: string[] = [];
  for (const field of fields) {
    const beforeValue = before?.[field];
    const afterValue = after?.[field];
    if (diff.changeType === 'created') {
      if (afterValue !== null && afterValue !== undefined && afterValue !== '') {
        rows.push(`${fieldLabel(field)}：${formatValue(afterValue)}`);
      }
      continue;
    }
    if (JSON.stringify(beforeValue ?? null) !== JSON.stringify(afterValue ?? null)) {
      rows.push(`${fieldLabel(field)}：${formatValue(beforeValue)} → ${formatValue(afterValue)}`);
    }
  }
  return rows.length ? rows : [tx('legacy:pages.admin.butreepage.onlyMetadataChanged')];
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function summaryRecord(job: SyncJob) {
  return asRecord(asRecord(job.summary)?.changes);
}

function summaryCount(job: SyncJob, entity: 'users' | 'departments') {
  const record = asRecord(summaryRecord(job)?.[entity]);
  return Object.values(record ?? {}).reduce<number>((sum, value) => sum + asNumber(value), 0);
}

function fieldLabel(field: string) {
  const map: Record<string, string> = {
    fullName: tx('legacy:pages.admin.butreepage.fieldFullName'),
    email: tx('legacy:pages.admin.butreepage.fieldEmail'),
    domainName: tx('legacy:pages.admin.butreepage.fieldAccount'),
    businessUnitNames: tx('legacy:pages.admin.butreepage.fieldBusinessUnit'),
    businessUnitName: tx('legacy:pages.admin.butreepage.fieldBusinessUnit'),
    status: tx('legacy:pages.admin.butreepage.fieldStatus'),
    syncState: tx('legacy:pages.admin.butreepage.fieldSyncState'),
    name: tx('legacy:pages.admin.butreepage.fieldDepartmentName'),
    parentName: tx('legacy:pages.admin.butreepage.fieldParentDepartment'),
    sortOrder: tx('legacy:pages.admin.butreepage.fieldSortOrder'),
  };
  return map[field] ?? field;
}

function formatValue(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return tx('legacy:pages.admin.butreepage.emptyValue');
  }
  if (value === 'active') return tx('legacy:pages.admin.butreepage.statusActive');
  if (value === 'disabled') return tx('legacy:pages.admin.butreepage.statusDisabled');
  if (value === 'departed') return tx('legacy:pages.admin.butreepage.statusDeparted');
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join('、') : tx('legacy:pages.admin.butreepage.emptyValue');
  }
  return String(value);
}
